import i18next from "i18next";
import { BaseFilter } from "../../supportFunctions/graphql/Queries";
import { User } from "../UserManagement/User";

export enum Gender {
  MALE = "male",
  FEMALE = "female",
}

export const genderSelectOptions = () => [
  {
    value: Gender.MALE,
    label: i18next.t("universal_Male"),
    id: Gender.MALE,
  },
  {
    value: Gender.FEMALE,
    label: i18next.t("universal_Female"),
    id: Gender.FEMALE,
  },
];

export const mapGenderToOption = (
  gender: string | undefined
): OptionType | undefined => {
  return genderSelectOptions().find((option) => option.value === gender);
};

export type OptionType = {
  id: string;
  label: string;
  value: string;
};

export type ID = string; // Add rules related to a ID type

export type ContractingCompany = {
  customer?: { id?: string };
  id: string;
  name: string;
};

export interface Person {
  id?: string;
  firstName?: string;
  lastName?: string;
  relationshipToCustomer?: OptionType;
  customerId: string;
  middleName?: string;
  displayName?: string;
  address2?: string;
  address3?: string;
  address4?: string;
  birthDate?: Date | string;
  city?: string;
  contractingCompany?: ContractingCompany;
  country?: string;
  currentPosition?: string;
  currentPositionStartDate?: Date | string;
  effectiveDate?: Date | string;
  email?: string;
  active?: boolean;
  employeeID?: string;
  gender?: string | OptionType;
  hireDate?: Date | string;
  phone?: string;
  primaryLocation?: OptionType;
  stateProv?: string;
  streetAddress?: string;
  supervisor?: Supervisor;
  zipPostal?: string;
  withUser?: boolean;
  userId?: string;
  faxNumber?: string;
  yearsInJob?: number;
  endDate?: Date | string;
  username?: string;
}

interface PersonSensitive {
  address2?: string;
  address3?: string;
  address4?: string;
  birthDate?: string;
  city?: string;
  country?: string;
  currentPosition?: string;
  currentPositionStartDate?: string;
  effectiveDate?: string;
  gender?: string | OptionType;
  hireDate?: string;
  id: string;
  stateProv?: string;
  streetAddress?: string;
  zipPostal?: string;
}

export interface PersonAttr {
  active?: boolean;
  contractingCompany?: ContractingCompany;
  displayName?: string;
  effectiveDate?: string;
  email?: string;
  employeeID?: string;
  endDate?: string;
  faxNumber?: string;
  firstName: string;
  id: string;
  lastName: string;
  middleName?: string;
  personIdentifier?: string;
  personSensitive?: PersonSensitive;
  phone?: string;
  primaryLocation?: string;
  relationshipToCustomer: string;
  status?: string;
  supervisor?: Supervisor;
  user?: User;
  userId?: string;
  yearsInJob?: number;
}

export type UserWithPerson = {
  username: string;
  customerId: string; //required
  relationshipToCustomer?: string; //required
  active?: boolean;
  password?: string;
} & Person;

export type Supervisor = {
  id: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  userId?: string;
};

export const DefaultPerson: Person = {
  id: "",
  customerId: "",
  firstName: "",
  lastName: "",
  relationshipToCustomer: undefined,
  middleName: "",
  displayName: "",
  address2: "",
  address3: "",
  address4: "",
  city: "",
  country: "",
  currentPosition: undefined,
  email: "",
  employeeID: "",
  gender: undefined,
  phone: "",
  primaryLocation: undefined,
  stateProv: "",
  streetAddress: "",
  zipPostal: "",
};

export type PersonFilterParams = {
  birthDate?: [Date];
  customerId?: [string];
  displayName?: [string];
  email?: [string];
  firstName?: [string];
  id?: [string];
  isUser?: boolean;
  lastName?: [string];
  middleName?: [string];
  phone?: [string];
  primaryLocation?: [string];
};

export enum UserStatus {
  Active = "Active",
  Inactive = "Inactive",
}

export const userStatusOptions = [
  { id: "active", label: UserStatus.Active, value: 1 },
  { id: "inactive", label: UserStatus.Inactive, value: 0 },
];

export const PersonStatusOptions = userStatusOptions.map((option) => ({
  id: option.label?.toUpperCase(),
  label: option.label,
}));

export const userStatusOptionDisplay = (rowData: any) => {
  if (!rowData) return "No data available";
  return rowData.label;
};

export interface PersonUserForm {
  id: string;
  displayedLoginName: string | undefined;
  active: boolean;
  userDemographic: { email: string };
  customerId: string;
  customer: {
    customerDomainName: string;
  };
}

export interface CreatePersonInput {
  address2?: string;
  address3?: string;
  address4?: string;
  birthDate?: string;
  city?: string;
  contractingCompany?: string;
  country?: string;
  currentPosition?: string;
  currentPositionStartDate?: string;
  customerId: string;
  displayName: string;
  effectiveDate?: string;
  email?: string;
  employeeID?: string;
  endDate?: string;
  faxNumber?: string;
  firstName?: string;
  gender?: string | OptionType;
  hireDate?: string;
  lastName?: string;
  middleName?: string;
  personIdentifier?: string;
  phone?: string;
  primaryLocation?: string;
  relationshipToCustomer: string;
  stateProv?: string;
  streetAddress?: string;
  supervisor?: string;
  yearsInJob?: number;
  zipPostal?: string;
}

export interface UpdatePersonInput {
  address2?: string;
  address3?: string;
  address4?: string;
  birthDate?: string;
  city?: string;
  contractingCompany?: string;
  country?: string;
  currentPosition?: string;
  currentPositionStartDate?: string;
  displayName?: string;
  effectiveDate?: string;
  email?: string;
  employeeID?: string;
  endDate?: string;
  faxNumber?: string;
  firstName?: string;
  gender?: string | OptionType;
  hireDate?: string;
  id: string;
  lastName?: string;
  middleName?: string;
  personIdentifier?: string;
  phone?: string;
  primaryLocation?: string;
  relationshipToCustomer?: string;
  stateProv?: string;
  streetAddress?: string;
  supervisor?: string;
  yearsInJob?: number;
  zipPostal?: string;
}

export interface IPersonUserForm {
  displayName: string;
  firstName?: string;
  lastName?: string;
  username: string;
  email: string;
  relationshipToCustomer: OptionType;
  contractingCompany?: OptionType;
}

export interface CreateUserInput {
  customerId: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  username: string;
  email: string;
  relationshipToCustomer: string;
  contractingCompany?: string;
}

export interface CreateUserFromPersonInput {
  customerId: string;
  personId: string;
  username: string;
  email: string;
  //Remove this fields when BE fixes the crateUser from person.
  firstName?: string;
  lastName?: string;
}

export interface ActivateInactivateUserInput {
  active: boolean;
  id: string;
  username?: string;
}

export type PersonFilter = BaseFilter<PersonFilterParams>;
